// Web port of pilotapp's src/utils/tileUtils.ts (calculateBoundsFromTiles).
// The native app scans a filesystem directory of {z}/{x}/{y}.png tiles; the web
// build derives the same bounds from the tile keys of the unzipped tile set.

export function tile2lng(x: number, z: number): number {
  return (x / Math.pow(2, z)) * 360 - 180
}

export function tile2lat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

export interface TileKey {
  z: number
  x: number
  y: number
}

export interface TileBoundsResult {
  bounds: [[number, number], [number, number]]
  minZoom: number
  maxZoom: number
}

// Mirrors pilotapp calculateBoundsFromTiles: bounds are computed from the highest
// zoom level present (most precise), using the standard XYZ → lng/lat conversion.
// Y is inverted (0 = north, max = south).
export function calculateBoundsFromTileKeys(keys: TileKey[]): TileBoundsResult | null {
  if (keys.length === 0) return null

  const zoomLevels = Array.from(new Set(keys.map((k) => k.z))).sort((a, b) => a - b)
  const minZoom = zoomLevels[0]
  const maxZoom = zoomLevels[zoomLevels.length - 1]

  const atMaxZoom = keys.filter((k) => k.z === maxZoom)
  if (atMaxZoom.length === 0) return null

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const { x, y } of atMaxZoom) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }

  const minLng = tile2lng(minX, maxZoom)
  const maxLng = tile2lng(maxX + 1, maxZoom) // +1 to reach the right edge
  const maxLat = tile2lat(minY, maxZoom) // minY = top (north)
  const minLat = tile2lat(maxY + 1, maxZoom) // maxY = bottom (south)

  return {
    bounds: [
      [minLng, minLat],
      [maxLng, maxLat]
    ],
    minZoom,
    maxZoom
  }
}
